/** @jsxImportSource @emotion/react */
import type { GridProps } from '@mui/material';
import {
  AppBar as MuiAppBar,
  Grid as MuiGrid,
  Toolbar as MuiToolbar,
} from '@mui/material';
import { Buttons } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { Hidden, UserMenu, ActivationExitDialog } from 'components';
import { Constants, css, theme } from 'global';
import { FC, useState } from 'react';
import * as styles from './BasicHeader.styles';
import { PageRoute } from 'enums';
import { useNavigate } from 'react-router-dom';

export interface BasicHeaderProps {
  alignLogoDesktop: GridProps['justifyContent'];
  helpButton: 'Expanded' | 'Minimized' | 'None';
  isAuthenticated: boolean;
  bottomBorder: boolean;
  shouldShowActivation: boolean;
  openHelpCentre?: () => void;
  showCloseButton?: boolean;
  onCloseButtonClick?: () => void;
  customOnExitDialogText?: string;
  showExitConfirmationDialog?: boolean;
}

const BasicHeader: FC<BasicHeaderProps> = ({
  alignLogoDesktop,
  helpButton,
  isAuthenticated,
  bottomBorder,
  shouldShowActivation,
  openHelpCentre,
  showCloseButton = false,
  onCloseButtonClick,
  customOnExitDialogText,
  showExitConfirmationDialog,
}) => {
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const navigate = useNavigate();
  const isCenter = alignLogoDesktop === 'center';
  const getLogo = ({ isMobile = false }) => (
    <img
      src={Constants.assets.logo.light}
      height={isMobile ? (isCenter ? 20.92 : 28) : isCenter ? 32 : 28}
      title="logo"
      alt="Logo"
      css={!isMobile ? styles.logo(isCenter) : undefined}
    />
  );

  const logo = getLogo({ isMobile: false });
  const mobileLogo = getLogo({ isMobile: true });

  const handleReturnToDashboard = () => {
    navigate(PageRoute.Home);
  };

  return (
    <>
      <MuiAppBar
        position="static"
        elevation={0}
        css={styles.appbar(bottomBorder)}
      >
        <MuiToolbar css={styles.toolbar}>
          <MuiGrid
            container
            justifyContent={isCenter ? 'center' : 'space-between'}
            sx={{ width: '100%', margin: 0 }}
          >
            {/* Desktop */}
            <Hidden xsDown>
              <MuiGrid
                container
                justifyContent={alignLogoDesktop}
                alignContent={'center'}
                sx={{ width: 'auto' }}
              >
                {shouldShowActivation ? (
                  <button
                    type="button"
                    css={styles.logoButton}
                    onClick={() => {
                      if (showExitConfirmationDialog) {
                        setIsExitConfirmationOpen(true);
                      }
                    }}
                    aria-label="Exit activation and return to dashboard"
                  >
                    {logo}
                  </button>
                ) : (
                  logo
                )}
              </MuiGrid>
            </Hidden>

            {/* Mobile */}
            <Hidden xsUp>
              <MuiGrid
                container
                justifyContent={isCenter ? 'center' : 'flex-start'}
                alignItems="center"
                css={
                  isCenter
                    ? css.flex('row', 'center', 'center')
                    : css.flex('row', 'flex-start', 'center', 'nowrap')
                }
                sx={{
                  display: isCenter && { width: '100%' },
                }}
              >
                {shouldShowActivation ? (
                  <button
                    type="button"
                    css={styles.logoButton}
                    onClick={() => {
                      if (showExitConfirmationDialog) {
                        setIsExitConfirmationOpen(true);
                      }
                    }}
                    aria-label="Exit activation and return to dashboard"
                  >
                    {mobileLogo}
                  </button>
                ) : (
                  mobileLogo
                )}
              </MuiGrid>
            </Hidden>

            {!isCenter && (
              <MuiGrid
                container
                sx={{ flexGrow: 1 }}
                size={{ xs: 5, md: 8 }}
                justifyContent="flex-end"
                alignItems="center"
                css={css.flex('row', 'flex-end', 'center', 'nowrap')}
              >
                {helpButton === 'Expanded' && (
                  <Buttons.Text
                    onClick={openHelpCentre}
                    css={styles.helpButton}
                  >
                    <div>
                      <Icons.QuestionCircle width={30} height={30} />
                      <span>Help</span>
                    </div>
                  </Buttons.Text>
                )}
                {helpButton === 'Minimized' && (
                  <Icons.QuestionCircle
                    width={40}
                    height={40}
                    fill={theme.colors.neutral[600]}
                    css={css.backgroundIcon}
                    onClick={openHelpCentre}
                  />
                )}
                {showCloseButton && (
                  <button
                    type="button"
                    css={styles.closeButton}
                    onClick={
                      onCloseButtonClick ??
                      (showExitConfirmationDialog
                        ? () => setIsExitConfirmationOpen(true)
                        : undefined)
                    }
                    aria-label="Close"
                  >
                    <Icons.Close width={20} height={20} />
                  </button>
                )}
                {/* User menu */}
                {isAuthenticated && !showCloseButton && (
                  <UserMenu background={true} />
                )}
              </MuiGrid>
            )}
          </MuiGrid>
        </MuiToolbar>
      </MuiAppBar>

      {showExitConfirmationDialog && isExitConfirmationOpen && (
        <ActivationExitDialog
          open={isExitConfirmationOpen}
          onCancelExit={() => setIsExitConfirmationOpen(false)}
          onConfirmExit={handleReturnToDashboard}
          customOnExitDialogText={customOnExitDialogText}
        />
      )}
    </>
  );
};

export default BasicHeader;
